import React, { useMemo } from 'react';
import { TextBox } from '../../../components/ui/TextBox';
import { Button } from '../../../components/ui/Button';
import { Table, Column } from '../../../components/ui/Table';
import { ComboBoxWithSearch, Option } from '../../../components/ui/ComboBoxWithSearch';
import { Transaction } from '../../../types/transactions';
import { IconButton, EyeIcon } from '../../../components/ui/IconButton';
import { Machine } from '../../../types/machines';
import { formatRupiah } from '../../../utils/format';
import * as XLSX from 'xlsx';

export type ListViewProps = {
  data: Transaction[];
  filteredData: Transaction[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (p: number) => void;
  setPageSize: (s: number) => void;
  query: string;
  setQuery: (q: string) => void;
  machineFilter: string;
  setMachineFilter: (v: string) => void;
  machines: Machine[];
  dateFrom: string;
  setDateFrom: (v: string) => void;
  dateTo: string;
  setDateTo: (v: string) => void;
  loading?: boolean;
  error?: string | null;
  onView: (id: string) => void;
};

export const TransactionsListView: React.FC<ListViewProps> = props => {
  const machineOptions: Option[] = useMemo(() => {
    return [
      { label: 'All Machines', value: '' },
      ...props.machines.map(m => ({ label: `#${m.id} ${m.name}`, value: String(m.id) })),
    ];
  }, [props.machines]);

  const exportExcel = () => {
    const headers = ['ID', 'Date', 'Machine', 'Product', 'Qty', 'Unit Price', 'Currency', 'Total', 'Order ID'];
    const rows = props.filteredData.map(t => [
      t.id,
      t.createdAt ? new Date(t.createdAt).toLocaleString() : '',
      t.machine?.name || t.machineId,
      t.product?.name || t.productId,
      t.qty,
      t.unitPrice,
      t.currency,
      t.qty * t.unitPrice,
      t.orderId ?? '',
    ]);

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Transactions');
    XLSX.writeFile(wb, `transactions_${Date.now()}.xlsx`);
  };

  const columns: Column<Transaction>[] = [
    { key: 'createdAt', header: 'Date', render: r => r.createdAt ? new Date(r.createdAt).toLocaleString() : '-', width: 180 },
    { key: 'machineId', header: 'Machine', render: r => r.machine?.name || r.machineId },
    { key: 'productId', header: 'Product', render: r => r.product?.name || r.productId },
    { key: 'qty', header: 'Qty', width: 80 },
    { key: 'unitPrice', header: 'Unit Price', width: 120, render: r => formatRupiah(r.unitPrice) },
    { key: 'total', header: 'Total', width: 120, render: r => formatRupiah(r.qty * r.unitPrice) },
    { key: 'actions', header: 'Actions', width: 80, render: (row: Transaction) => (
      <div className="flex items-center gap-2">
        <IconButton title="View" onClick={() => props.onView(row.id)}>
          <EyeIcon />
        </IconButton>
      </div>
    )},
  ];

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h1 className="text-xl font-semibold">Transactions</h1>
        <div className="flex items-center gap-2">
          <Button onClick={exportExcel}>Export Excel</Button>
        </div>
      </div>
      <div className="mb-3 text-xs text-muted-foreground">
        Transactions are generated by vending machines and are read-only in Admin. Creating or updating transactions from this dashboard is not available.
      </div>

      <div className="mb-3 grid grid-cols-1 gap-2 md:grid-cols-4">
        <TextBox placeholder="Search by product/order" value={props.query} onChange={e => props.setQuery(e.target.value)} />
        <ComboBoxWithSearch label="Machine" options={machineOptions} value={props.machineFilter} onChange={v => props.setMachineFilter(String(v ?? ''))} />
        <TextBox type="date" label="From" value={props.dateFrom} onChange={e => props.setDateFrom(e.target.value)} />
        <TextBox type="date" label="To" value={props.dateTo} onChange={e => props.setDateTo(e.target.value)} />
      </div>

      {props.error && <div className="mb-2 text-sm text-destructive">{props.error}</div>}
      <Table
        columns={columns}
        data={props.data}
        page={props.page}
        pageSize={props.pageSize}
        total={props.total}
        onPageChange={props.setPage}
        onPageSizeChange={props.setPageSize}
      />
    </div>
  );
};

export default TransactionsListView;
